import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Spinner, Alert, Modal, ModalBody, Badge } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../hooks/useProducts';
import { ProductForm } from './ProductForm';
import { ProductImportModal } from './ProductImportModal';
import type { Product } from '../types/product.types';
import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';

export function ProductListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'code'>('name');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const queryClient = useQueryClient();

  // Debounce search input: wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if there are at least 3 characters or if it's empty (to show all)
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setDebouncedSearch(searchInput);
        setPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useProducts({
    page,
    // Nota: Aludra ignora el limit y usa su propio (20 por defecto)
    name: searchType === 'name' ? debouncedSearch : undefined,
    code: searchType === 'code' ? debouncedSearch : undefined
  });

  // Debug: Log when page or data changes
  useEffect(() => {
    console.log('📄 Page or data changed:', {
      page,
      dataPage: data?.page,
      productsCount: data?.products.length,
      total: data?.total,
      totalPages: data?.totalPages,
      firstProductSKU: data?.products[0]?.sku,
      lastProductSKU: data?.products[data?.products.length - 1]?.sku,
    });
  }, [page, data]);

  // El backend ya pagina, usamos directamente los productos que devuelve
  // Aludra usa su propio limit (20 por defecto), respetamos lo que devuelve
  const limit = data?.limit || 20;
  const totalPages = data?.totalPages || 1;
  const products = data?.products || [];

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const { validateCanAddProduct } = useConsumption();

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleImportSuccess = () => {
    // Invalidate and refetch products
    queryClient.invalidateQueries({ queryKey: ['products'] });
    Swal.fire({
      title: 'Importación exitosa',
      text: 'Los productos han sido importados correctamente',
      icon: 'success',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6',
    });
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (data: Partial<Product>) => {
    console.log('📝 handleSubmit - Start', { editingProduct, data });

    // Only validate when creating (not editing)
    if (!editingProduct) {
      console.log('🔍 Validando límite de productos...');
      const validation = validateCanAddProduct();
      console.log('✅ Resultado de validación:', validation);

      if (!validation.allowed) {
        console.log('❌ No permitido - Mostrando alerta');
        const result = await Swal.fire({
          title: 'Límite de productos alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
          // Navigate to plans page
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
      console.log('✅ Validación pasada - Procediendo a crear producto');
    }

    try {
      if (editingProduct) {
        console.log('📝 Actualizando producto...');
        await updateMutation.mutateAsync({ id: editingProduct.id, data });
        handleCloseModal();

        // Success message for update
        Swal.fire({
          title: '¡Actualizado!',
          text: 'El producto ha sido actualizado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      } else {
        console.log('➕ Creando producto...');
        await createMutation.mutateAsync(data);
        handleCloseModal();

        // Success message for create
        Swal.fire({
          title: '¡Creado exitosamente!',
          text: 'El producto ha sido creado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      }
      console.log('✅ Producto guardado exitosamente');
    } catch (error) {
      console.error('❌ Error al guardar producto:', error);

      // Error message
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al guardar el producto. Por favor, intenta de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleDelete = async (product: Product) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará el producto "${product.name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await deleteMutation.mutateAsync(product.id);
      Swal.fire({
        title: 'Eliminado',
        text: 'El producto ha sido eliminado exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar productos">
        Ocurrió un error al cargar los productos. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Productos</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Catálogo de productos y servicios
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 [&>*]:w-full sm:[&>*]:w-auto">
          <Button
            data-guide="Importar Productos"
            variant="outline"
            leftIcon={<ArrowUpTrayIcon className="w-5 h-5" />}
            onClick={() => setIsImportModalOpen(true)}
          >
            Importar Excel
          </Button>
          <Button data-guide="Nuevo Producto" leftIcon={<PlusIcon className="w-5 h-5" />} onClick={handleOpenCreate}>
            Nuevo Producto
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardBody className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Search Type Selector */}
            <div className="flex rounded-lg border border-secondary-300 dark:border-secondary-600 overflow-hidden">
              <button
                onClick={() => setSearchType('name')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  searchType === 'name'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                }`}
              >
                Nombre
              </button>
              <button
                onClick={() => setSearchType('code')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-secondary-300 dark:border-secondary-600 ${
                  searchType === 'code'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                }`}
              >
                Código
              </button>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
              </div>
              <input
                type="text"
                placeholder={`Buscar por ${searchType === 'name' ? 'nombre' : 'código'} (mínimo 3 caracteres)...`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Search Status Messages */}
            {searchInput.length > 0 && searchInput.length < 3 && (
              <span className="text-sm text-secondary-500 dark:text-secondary-400">
                Escribe al menos 3 caracteres para buscar
              </span>
            )}
            {searchInput !== debouncedSearch && searchInput.length >= 3 && (
              <span className="text-sm text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                <Spinner size="sm" />
                Buscando...
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay productos
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                {debouncedSearch ? 'No se encontraron resultados para tu búsqueda' : 'Comienza creando tu primer producto'}
              </p>
              {!debouncedSearch && (
                <div className="mt-6">
                  <Button onClick={handleOpenCreate}>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nuevo Producto
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Código Impuesto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">
                      {product.sku}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{product.name}</div>
                        {product.description && (
                          <div className="text-sm text-secondary-500 dark:text-secondary-400">
                            {product.description.substring(0, 50)}
                            {product.description.length > 50 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.isService ? 'info' : 'default'} size="sm">
                        {product.isService ? 'Servicio' : 'Producto'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {product.category && (
                        <Badge variant="default" size="sm">
                          {product.category}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {product.currency || 'USD'} {product.price != null ? product.price.toFixed(2) : '0.00'}
                      {product.priceIncludesTax && (
                        <span className="text-xs text-secondary-500 ml-1">(inc. imp.)</span>
                      )}
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {product.taxCode || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {!product.isDeleted ? (
                        <Badge variant="success" size="sm">
                          Activo
                        </Badge>
                      ) : (
                        <Badge variant="danger" size="sm">
                          Eliminado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(product)}
                          className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="text-danger-600 hover:text-danger-900 dark:hover:text-danger-400"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && products.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {((page - 1) * limit) + 1} a{' '}
                  {Math.min(page * limit, data.total)} de {data.total} productos
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {totalPages}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    console.log('📄 Clicking next, current page:', page, 'going to:', page + 1);
                    setPage(page + 1);
                  }}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        size="4xl"
      >
        <ModalBody className="p-6">
          <ProductForm
            product={editingProduct || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </ModalBody>
      </Modal>

      {/* Modal Import Excel */}
      <ProductImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
